import type { LinkedCryptoAsset, QuantumRisk } from '@/api/types'
import { RiskBadge } from '@/pages/Packages'

const NIST_REF: Record<string, string> = {
  RSA: 'NIST FIPS 203/204',
  'RSA-2048': 'NIST FIPS 203',
  'RSA-4096': 'NIST FIPS 203',
  ECDH: 'NIST FIPS 203',
  ECDSA: 'NIST FIPS 204',
  DSA: 'NIST FIPS 204',
  DH: 'NIST FIPS 203',
  'Diffie-Hellman': 'NIST FIPS 203',
}

function advisoryId(name: string): string {
  return `QUANTUM-RISK-${name.trim().toUpperCase().replace(/[\s/]+/g, '-')}`
}

function nistRef(name: string): string | undefined {
  for (const [key, ref] of Object.entries(NIST_REF)) {
    if (name.toUpperCase().includes(key.toUpperCase())) return ref
  }
  return undefined
}

interface VexPanelProps {
  evidence: LinkedCryptoAsset[]
}

export default function VexPanel({ evidence }: VexPanelProps) {
  if (!evidence.length) return null

  const quantum = evidence.filter((e) => !e.is_quantum_safe)
  const safe = evidence.filter((e) => e.is_quantum_safe)

  return (
    <div className="space-y-3">
      {quantum.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-2 uppercase tracking-wide">
            Quantum Risk Advisories ({quantum.length})
          </p>
          <div className="space-y-1.5">
            {quantum.map((e) => {
              const id = advisoryId(e.name)
              const ref = nistRef(e.name)
              return (
                <div
                  key={e.id}
                  className="flex flex-col gap-1 text-xs bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-error">{id}</span>
                    <RiskBadge risk={e.quantum_risk as QuantumRisk} />
                    {e.confidence && (
                      <span className="text-subtle-light dark:text-subtle-dark">conf: {e.confidence}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-subtle-light dark:text-subtle-dark">
                    <span>{e.asset_type}</span>
                    {ref && (
                      <span className="text-warning font-medium">{ref}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {safe.length > 0 && (
        <div>
          <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark mb-2 uppercase tracking-wide">
            PQC-Safe Crypto Links ({safe.length})
          </p>
          <div className="space-y-1">
            {safe.map((e) => (
              <div
                key={e.id}
                className="flex items-center gap-3 text-xs bg-background-light dark:bg-background-dark rounded px-3 py-2"
              >
                <span className="font-medium text-text-light dark:text-text-dark">{e.name}</span>
                <span className="text-subtle-light dark:text-subtle-dark">{e.asset_type}</span>
                <RiskBadge risk={e.quantum_risk as QuantumRisk} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
